/**
 * DraftPasteBox — the whole league, off the clipboard, in one step.
 *
 * 🔴 THIS IS THE FASTEST PATH ON THE PAGE AND IT SITS AT THE TOP FOR THAT
 * REASON. Ben, Sep 1 2026: "If I go to my yahoo league on PC and just hit
 * control A and copy the draft results, would that be easy for you to get the
 * results from?" A screenshot gives one roster at reduced accuracy; a paste
 * gives ten rosters at full accuracy, with the prices exact and the team names
 * attached. Every other route on this page exists for people who cannot take
 * this one.
 *
 * 🔴 IT SHOWS THE ROOM BEFORE IT FILLS ANYTHING. The paste replaces every seat,
 * which is a large thing to do to somebody's work, so the seats and their spend
 * are printed first and a button does the filling. Nothing is destroyed by a
 * paste alone.
 *
 * 🔴 AND IT REFUSES RATHER THAN GUESSING. Text with no draft in it, or one
 * team's worth of rows, does not become a league; it becomes a sentence saying
 * what to copy instead. A page that quietly invented a nine-team room out of
 * half a transcript would be worse than one that read nothing.
 *
 * THE TEXT NEVER LEAVES THE DEVICE. Parsing is local, exactly as the screenshot
 * reader's recognition is, and only the rosters the user then submits go
 * anywhere. That includes the analytics event this box fires: a paste is
 * nothing but player, team and manager names, so `rate_paste_parsed` carries a
 * boolean and, on a failure, one value from a closed enum — no excerpt, no
 * truncation, no sample line, not even a length. rateAnalytics.js holds the
 * rule and rateAnalytics.test.js is the fence.
 */

import { useMemo, useState } from "react";
import { theme } from "../../data/theme.js";
import { parseDraftPaste, pasteFailReason, pasteSummary } from "../../utils/pasteDraft.js";
import { captureRatePasteParsed } from "../../utils/rateAnalytics.js";
import { C, SZ, primaryBtn, sectionTitle } from "../Submit/submitStyles.js";

/**
 * 🔴 THE KEYSTROKES, NOT A DESCRIPTION OF THEM. Ben, Sep 1 2026: "You can
 * simply hit CTRL-A on the page, followed by CTRL-C (Copy). Come back to this
 * page and click in the box, hit CTRL-V (Paste)."
 *
 * "Select the page and copy it" is a sentence that assumes the reader already
 * knows this trick. Most people do not select an entire web page on purpose in
 * a normal week, and the ones who would benefit most from a one-paste import
 * are exactly the ones who have never done it. So the steps are literal, they
 * are numbered, and they are set at reading size rather than as fine print -
 * this is the instruction that makes the feature work, not a footnote to it.
 *
 * 🔴 ONLY THE PATH THAT HAS BEEN TESTED IS NAMED. Yahoo's is verified end to
 * end. Sleeper's and ESPN's are not, and a confidently wrong menu path would
 * send people somewhere useless with the app's own instructions behind it,
 * which is worse than saying nothing. Everything else is described by SHAPE -
 * any page listing every pick with what it cost.
 */
export const COPY_STEPS = [
  "On a computer, open your league and go to Draft, then Draft Results.",
  "Press Ctrl-A to select the whole page (Cmd-A on a Mac).",
  "Press Ctrl-C to copy it (Cmd-C on a Mac).",
  "Come back here, click in the box below, and press Ctrl-V to paste.",
];

/** Kept as its own line: it is a caveat, not a step. */
export const COPY_NOTE =
  "That is the Yahoo path. On any other site, any page that lists every pick with what it cost will work.";

/**
 * 🔴 THE TEAM TAB'S TWO EXTRA LINES, AND WHY THE BOX NEEDS A VARIANT AT ALL.
 * On the team tab this box rendered with the league tab's copy word for word, so
 * it read as the league feature leaking in: a visitor who came to rate ONE team
 * met a box about everybody else's rosters and nothing told them the manual path
 * below was a complete route rather than a fallback. It is complete — a solo team
 * grades against the real 2026 corpus on its own, and the room is only needed for
 * room-relative context.
 *
 * So the paste keeps its place as the SHORTCUT (it really is faster, and it fills
 * the room too), and the second line names the other door instead of leaving the
 * reader to infer it. League tab renders neither line and is byte-identical to
 * before.
 */
export const TEAM_LEAD =
  "The fastest way in: one paste fills your roster, your prices, and your whole room.";

export const TEAM_SOLO_NOTE =
  "Just want your own team rated, nothing else? Skip the paste and fill in your roster below.";

const box = {
  border: `1px solid ${C.border.default}`,
  borderRadius: theme.radius.lg,
  background: C.bg.deep,
  padding: 14,
  marginBottom: 12,
};

/**
 * `variant` is additive and defaults to absent: with no variant the league tab
 * renders exactly what it rendered before this prop existed. Only "team" adds
 * copy, and it adds copy ONLY — no layout, no handler, no parse behaviour.
 */
export default function DraftPasteBox({ onFill, variant }) {
  const isTeam = variant === "team";
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  // Parsing is cheap and local, so it happens as the text arrives: the user
  // sees their ten teams appear rather than pressing a button to find out.
  const read = useMemo(() => (text.trim() ? parseDraftPaste(text) : null), [text]);
  const ready = !!(read && read.ok);

  /**
   * One event per paste attempt, at the moment the parse decides.
   *
   * 🔴 FIRED FROM THE CLIPBOARD EVENT, NOT FROM AN EFFECT ON `text`, and the
   * reason is timing rather than taste. A paste is two browser events — the
   * paste, then the change that inserts it — so an effect keyed on the box's
   * value runs a render late and, when somebody re-pastes text byte-identical
   * to what is already there, does not run at all. Reading the clipboard here
   * gives exactly one event per attempt with no state coupling.
   *
   * The cost, stated: this reports the parse of what ARRIVED, which differs
   * from the parse of the box only when somebody pastes into a box that already
   * has text. The instructions send people to an empty box and a re-paste is
   * normally select-all first, so the two agree in every ordinary case.
   *
   * Parsing twice is deliberate and cheap — this file's own note is that
   * parsing is local and happens as the text arrives — and it is worth a second
   * pass to keep the measurement independent of React's scheduling.
   */
  function countParse(pastedText) {
    const attempt = parseDraftPaste(pastedText || "");
    captureRatePasteParsed({ ok: attempt.ok, reason: pasteFailReason(attempt) });
  }

  return (
    <div style={box} data-testid="draft-paste">
      <div data-testid="draft-paste-title" style={sectionTitle}>
        Paste your whole draft
      </div>
      <div
        data-testid="draft-paste-sub"
        style={{ marginTop: 6, fontSize: SZ.body, color: C.text.secondary, lineHeight: 1.55 }}
      >
        {isTeam ? `${TEAM_LEAD} ` : ""}
        Every team in your league, filled in at once, with the real prices. It takes about ten
        seconds.
      </div>

      {/* Reading size, numbered, with the actual keys in it. See COPY_STEPS. */}
      <ol
        data-testid="draft-paste-where"
        style={{
          margin: "10px 0 0",
          paddingLeft: 22,
          fontSize: SZ.body,
          color: C.text.secondary,
          lineHeight: 1.7,
        }}
      >
        {COPY_STEPS.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ol>

      <div style={{ marginTop: 8, fontSize: SZ.small, color: C.text.faint, lineHeight: 1.5 }}>
        {COPY_NOTE}
      </div>

      {isTeam && (
        <div
          data-testid="draft-paste-solo"
          style={{ marginTop: 8, fontSize: SZ.small, color: C.text.faint, lineHeight: 1.5 }}
        >
          {TEAM_SOLO_NOTE}
        </div>
      )}

      <textarea
        data-testid="draft-paste-input"
        aria-label="Paste your draft results"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onPaste={(e) => countParse(e.clipboardData && e.clipboardData.getData("text"))}
        placeholder="Paste here (Ctrl-V)"
        rows={5}
        style={{
          marginTop: 12,
          width: "100%",
          boxSizing: "border-box",
          resize: "vertical",
          padding: 12,
          // 16px is load-bearing on iOS: anything smaller zooms the viewport
          // on focus and drops the user into a magnified page. submitStyles
          // makes the same point about its own inputs.
          fontSize: 16,
          fontFamily: "inherit",
          color: C.text.primary,
          background: C.bg.raised,
          border: `1px solid ${C.border.default}`,
          borderRadius: theme.radius.lg,
        }}
      />

      {read && (
        <div
          data-testid="draft-paste-note"
          style={{
            marginTop: 10,
            fontSize: SZ.body,
            fontWeight: 700,
            lineHeight: 1.5,
            color: ready ? C.text.secondary : C.status.warning,
          }}
        >
          {pasteSummary(read)}
        </div>
      )}

      {/* 🔴 A FAILED PASTE SHOWS ITS OWN EVIDENCE, and this is the whole
          strategy for platforms nobody here has seen. Yahoo is verified. ESPN,
          Sleeper's board, NFL.com, MFL and Fantrax are not, and guessing their
          layouts is how the screenshot reader ended up pointing people at a
          page with no prices on it. So a paste that does not parse prints the
          three priced lines it found - which is exactly the shape somebody
          needs in order to teach this parser - and asks for them. One reply
          turns an unknown platform into a fixture.

          The lines are shown to the user BEFORE anything is sent, and nothing
          is sent from here at all; the copy asks them to send it themselves. */}
      {read && !ready && read.diagnostic.samples.length > 0 && (
        <div style={{ marginTop: 8 }} data-testid="draft-paste-diagnostic">
          <div style={{ fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.6 }}>
            It found {read.diagnostic.dollars} prices in {read.diagnostic.lines} lines but could
            not read the rows. If you send these lines to feedback@mock1.app, this will read your
            league next time:
          </div>
          <div
            style={{
              marginTop: 6,
              padding: 8,
              border: `1px solid ${C.border.default}`,
              borderRadius: theme.radius.lg,
              fontFamily: "monospace",
              fontSize: SZ.micro,
              color: C.text.secondary,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {read.diagnostic.samples.join("\n")}
          </div>
        </div>
      )}

      {ready && (
        <>
          {/* The seats, before anything is filled. Somebody scanning for their
              own team name can find it here and stop if this is the wrong
              league. Spend rides along because it is the number that shows the
              prices came through. */}
          <ul
            data-testid="draft-paste-teams"
            style={{
              margin: "8px 0 0",
              padding: 0,
              listStyle: "none",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {read.teams.map((t) => (
              <li
                key={t.name}
                style={{
                  fontSize: SZ.small,
                  padding: "4px 10px",
                  borderRadius: theme.radius.lg,
                  border: `1px solid ${C.border.default}`,
                  color: C.text.secondary,
                }}
              >
                {t.name} ${t.entries.reduce((a, e) => a + (e.price || 0), 0)}
              </li>
            ))}
          </ul>

          {read.unmatched.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                data-testid="draft-paste-missing-toggle"
                onClick={() => setOpen((v) => !v)}
                style={{
                  padding: 0,
                  fontSize: SZ.micro,
                  fontWeight: 700,
                  border: "none",
                  background: "transparent",
                  color: C.text.faint,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {open ? "Hide" : "Show"} the {read.unmatched.length} I do not have players for
              </button>
              {open && (
                <div
                  data-testid="draft-paste-missing"
                  style={{ marginTop: 6, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.6 }}
                >
                  {/* Printed, never guessed. A name this app's player list has
                      never heard of cannot be graded, and the honest thing is to
                      say which one rather than leave a hole in a roster. */}
                  {read.unmatched.map((u, i) => (
                    <div key={`${u.raw}-${i}`}>
                      {u.raw} · {u.pos} · ${u.price == null ? "?" : u.price}
                      {u.owner ? ` · ${u.owner}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            data-testid="draft-paste-fill"
            onClick={() => onFill(read, text)}
            style={{ ...primaryBtn(true), marginTop: 10 }}
          >
            {/* 🔴 IT NAMES THE OUTCOME, NOT THE MECHANISM. Ben, Sep 1 2026.
                "Fill in all 10 teams" described what the code was about to do
                to the form; this button now leads straight to the room grades,
                so it should say the thing the user came for. The count it used
                to carry is not lost - the line above says how many teams were
                found, and every seat is listed under it by name. */}
            Rate My League!
          </button>
        </>
      )}
    </div>
  );
}
